import { PaginationInfo, parseQueryParamsObject, removeDuplicateQueryParams } from "@robe/core-utils";
import { BaseApiRequest } from "@robe/core-api/baseApiRequest";
import { Content, PickContentValues } from "@robe/core-types";
import { XcoreCore } from "XcoreCore";
import { GetContentsOptions, GetContentsSource } from "./types";
import { paginateRes, PaginationParsed, resolvePagination } from "./utils/pagination";
import { getContentsBaseQueries } from "./utils/query";
import { resolveUnfetchedValuesToContentValuesForContents } from "./utils/resolveAndAssignUnfetchedValues";
import { resolveLocaleParams } from "@robe/core-api/content/utils/queryParamsResolvers";
import { GetMixedContentsOptions } from "./getMixedContents";
import { NotFoundError } from "../errors";
import { resolveContentsSubCategories } from "./utils/subCategories";

export interface ContentsResponse<T extends Content = Content, TKey extends keyof T["values"] = keyof T["values"]> {
  contents: Array<PickContentValues<T, TKey>>;
  options?: GetContentsOptions<T>;
  pagination?: PaginationInfo;
}

/**
 * Fetches content based on the provided types or IDs, request context, and optional options.
 * This function supports fetching content with specified fields only and pagination.
 *
 * @param source Determines whether content should be fetched with `types` or `ids` parameter. If neither one of them or both or them are provided error will be thrown.
 * @param source.types Content type or array of content types.
 * @param source.ids Content ID or array of content IDs.
 * @param core The XcoreCore instance.
 * @param options (Optional), options for fetching contents.
 * @param options.publishedOnly In web only published contents are being fetched by default
 * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
 * an object containing options for fetching content, such as sorting, categorization, and filtering.
 * @param options.sort (Optional),
 * content property or content field which the result should be sorted by.
 * Can be a `string`, a queryable object, or an array of these with a sort direction (`"desc"` or `"asc"`).
 * @param options.page (Optional), pagination page number.
 * @param options.perPage (Optional), how many results should be included in a page.
 * @param options.categories (Optional), Filter content by sub categories.
 * @param options.includeCategories (Optional), include categories in the contents.
 * @param options.createdAt (Optional), filter content by the date it was createdAt.
 * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
 * @param options.publishedAt (Optional), filter content by the date it was publishedAt. '
 * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
 * @param options.fields (Optional), list of specific content fields to fetch.
 * `includeValues=true` param is automatically being included in request if `fields` param is specified.
 * @param options.includeValues (Optional), include all content values. If used, it takes precedence over specified fields.
 * @param options.includeNonLocalized (Optional), include non localized contents even if `locale` param is provided.
 * @param paginate (Optional), Whether the response should be paginated. Defaults to `true`.
 *
 * @returns A function that, when called with an array of field names, returns a promise resolving to an `BaseApiRequest` of `ContentsResponse<T, TField>`.
 */
export const getContents = <T extends Content, TField extends keyof T["values"] & string>(
  source: GetContentsSource<T>,
  core: XcoreCore,
  fields: Array<TField> = [],
  options: GetContentsOptions<T> = {}
): BaseApiRequest<ContentsResponse<T, TField>> => {
  const url = removeDuplicateQueryParams(
    getContentsUrl<T>(core, source, {
      ...options,
      ...resolveLocaleParams(core, options?.locales),
      ...(fields.length > 0 && { fields, includeValues: true }),
    })
  );

  return core
    .fetch(url)
    .then(paginateRes)
    .then(async ([contents, pagination]: [Array<PickContentValues<T, TField>>, PaginationParsed]) => {
      const contentsWithUnfetchedValuesResolved = await resolveUnfetchedValuesToContentValuesForContents(contents, core);
      const contentsWithSubCategoriesResolved = resolveContentsSubCategories(contentsWithUnfetchedValuesResolved);

      return {
        contents: contentsWithSubCategoriesResolved as Array<PickContentValues<T, TField>>,
        options,
        ...resolvePagination(pagination, options?.perPage),
      };
    })
    .catch((err) => {
      if (err instanceof NotFoundError) {
        return {
          contents: [],
        };
      }

      throw err;
    });
};

/**
 * Constructs a URL for fetching content based on the provided context and options.
 *
 * @param source.types Content type or array of content types.
 * @param source.ids Content ID or array of content IDs.
 * @param options An object containing options for fetching content, such as sorting, categorization, and filtering.
 *
 * @returns A string representing the URL for fetching content, including query parameters for sorting, categorization, filtering, pagination, and context-specific options.
 */
export const getContentsUrl = <T extends Content, TFields extends object = object>(
  core: XcoreCore,
  source: GetContentsSource<T>,
  options: GetContentsOptions<T> | GetMixedContentsOptions<TFields>
) => {
  const queries = getContentsBaseQueries(core, source, options as GetContentsOptions<T>);

  const queriesWithMixedContentsQueries =
    "includeNonLocalized" in options
      ? queries === ""
        ? parseQueryParamsObject({ includeNonLocalized: options?.includeNonLocalized }, { omitQuestionMark: true })
        : `&${parseQueryParamsObject({ includeNonLocalized: options?.includeNonLocalized }, { omitQuestionMark: true })}`
      : "";

  const queriesMerged = queries + queriesWithMixedContentsQueries;

  return `/api/contents${queriesMerged !== "" ? `?${queriesMerged}` : ""}`;
};
